import { useState } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle, Copy, Phone, ArrowRight, Building2, Percent, Sparkles, Clock } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const businessTypes = [
  'Restaurant / Bar',
  'Real Estate',
  'Retail / Boutique',
  'Professional Services',
  'Healthcare',
  'Construction / Trades',
  'Marketing / Creative Agency',
  'Non-Profit',
  'Other',
];

const BusinessSignupPage = () => {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [formData, setFormData] = useState({
    business_name: '',
    owner_name: '',
    email: '',
    phone: '',
    business_type: '',
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(`${API}/business-signup`, formData);
      if (response.data.status === 'success') {
        setResult(response.data);
        toast.success('Welcome aboard! Your discount code is ready.');
      }
    } catch (error) {
      toast.error('Sign-up failed. Please try again or call us.');
    } finally {
      setLoading(false);
    }
  };

  const copyCode = () => {
    if (result?.discount_code) {
      navigator.clipboard.writeText(result.discount_code);
      toast.success('Discount code copied to clipboard');
    }
  };

  return (
    <div data-testid="business-signup-page">
      {/* Hero */}
      <section className="py-16 md:py-20 bg-[#3EACDB] text-white relative overflow-hidden" data-testid="business-hero">
        <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'radial-gradient(circle at 20% 50%, white 1px, transparent 1px)', backgroundSize: '24px 24px' }} aria-hidden="true" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full mb-6">
            <Sparkles className="w-4 h-4 text-yellow-300" />
            <span className="text-sm font-medium uppercase tracking-wider">Business Loyalty Program</span>
          </div>
          <h1 className="font-heading text-4xl md:text-6xl lg:text-7xl font-black tracking-tighter uppercase leading-[0.95]">
            Sign Up.<br />
            Save <span className="text-yellow-300">10%</span>.<br />
            Print Smarter.
          </h1>
          <p className="mt-6 text-lg text-blue-100 max-w-2xl">
            Register your business with Charles Print today and unlock an instant <strong>10% discount</strong> on every order — from business cards to large-format banners.
          </p>
        </div>
      </section>

      {/* Perks */}
      <section className="py-12 md:py-16 bg-white" data-testid="business-perks">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              {
                icon: Percent,
                title: '10% Off Every Order',
                desc: 'Your discount applies to all services — business cards, flyers, banners, custom jobs.',
                image: 'https://images.unsplash.com/photo-1607082348824-0a96f2a4b9da?auto=format&fit=crop&w=800&q=80',
                alt: 'Discount shopping bags representing 10% savings',
              },
              {
                icon: Clock,
                title: 'Priority Same-Day Service',
                desc: 'Business members get first access to our same-day delivery queue.',
                image: 'https://images.unsplash.com/photo-1501139083538-0139583c060f?auto=format&fit=crop&w=800&q=80',
                alt: 'Hourglass representing fast same-day service',
              },
              {
                icon: Building2,
                title: 'Dedicated Account Support',
                desc: 'Direct line to our team for repeat orders, custom quotes, and design help.',
                image: 'https://images.unsplash.com/photo-1521791136064-7986c2920216?auto=format&fit=crop&w=800&q=80',
                alt: 'Business handshake representing dedicated account support',
              },
            ].map((perk) => (
              <div key={perk.title} className="bg-zinc-50 border border-zinc-200 hover:border-[#3EACDB]/40 transition-colors overflow-hidden flex flex-col">
                <div className="relative w-full aspect-[4/3] overflow-hidden bg-zinc-100">
                  <img
                    src={perk.image}
                    alt={perk.alt}
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                </div>
                <div className="p-6 flex flex-col flex-1">
                  <div className="w-12 h-12 bg-[#3EACDB]/10 rounded-sm flex items-center justify-center mb-4">
                    <perk.icon className="w-6 h-6 text-[#3EACDB]" />
                  </div>
                  <h3 className="font-heading text-lg font-bold mb-2">{perk.title}</h3>
                  <p className="text-zinc-600 text-sm leading-relaxed">{perk.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Form / Success */}
      <section className="py-16 md:py-24 bg-zinc-50" data-testid="business-form-section">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          {!result ? (
            <form onSubmit={handleSubmit} className="bg-white border border-zinc-200 p-8 md:p-10 shadow-sm" data-testid="business-signup-form">
              <h2 className="font-heading text-3xl md:text-4xl font-black uppercase tracking-tight mb-2">Sign Up Your Business</h2>
              <p className="text-zinc-600 mb-8">Takes 30 seconds. Your discount code is delivered instantly.</p>

              <div className="space-y-5">
                <div>
                  <Label htmlFor="business_name" className="text-sm font-semibold uppercase tracking-wider">Business Name *</Label>
                  <Input id="business_name" name="business_name" required value={formData.business_name} onChange={handleChange} className="mt-2 h-12" placeholder="Acme Coffee Co." data-testid="input-business-name" />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <Label htmlFor="owner_name" className="text-sm font-semibold uppercase tracking-wider">Your Name *</Label>
                    <Input id="owner_name" name="owner_name" required value={formData.owner_name} onChange={handleChange} className="mt-2 h-12" placeholder="Jane Doe" data-testid="input-owner-name" />
                  </div>
                  <div>
                    <Label htmlFor="phone" className="text-sm font-semibold uppercase tracking-wider">Phone</Label>
                    <Input id="phone" name="phone" type="tel" value={formData.phone} onChange={handleChange} className="mt-2 h-12" placeholder="(212) 555-1234" data-testid="input-phone" />
                  </div>
                </div>

                <div>
                  <Label htmlFor="email" className="text-sm font-semibold uppercase tracking-wider">Business Email *</Label>
                  <Input id="email" name="email" type="email" required value={formData.email} onChange={handleChange} className="mt-2 h-12" placeholder="you@yourbusiness.com" data-testid="input-email" />
                </div>

                <div>
                  <Label htmlFor="business_type" className="text-sm font-semibold uppercase tracking-wider">Business Type</Label>
                  <select id="business_type" name="business_type" value={formData.business_type} onChange={handleChange} className="mt-2 h-12 w-full border border-input bg-background px-3 py-2 text-base ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-md" data-testid="input-business-type">
                    <option value="">Select one (optional)</option>
                    {businessTypes.map((t) => (
                      <option key={t} value={t}>{t}</option>
                    ))}
                  </select>
                </div>
              </div>

              <Button type="submit" disabled={loading} className="mt-8 w-full h-14 text-base font-bold uppercase tracking-wider bg-[#3EACDB] hover:bg-[#2593C4] rounded-sm" data-testid="business-signup-submit">
                {loading ? 'Submitting…' : 'Get My 10% Discount'}
                {!loading && <ArrowRight className="ml-2 w-5 h-5" />}
              </Button>

              <p className="text-xs text-zinc-500 mt-4 text-center">We respect your privacy. Your info is used only to honor your discount and notify you of relevant offers.</p>
            </form>
          ) : (
            <div className="bg-white border border-zinc-200 p-8 md:p-12 shadow-sm text-center" data-testid="business-signup-success">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-10 h-10 text-green-600" />
              </div>
              <h2 className="font-heading text-3xl md:text-4xl font-black uppercase tracking-tight mb-3">You're In!</h2>
              <p className="text-zinc-600 mb-8 max-w-md mx-auto">
                Welcome to the Charles Print Business Program. Your 10% discount is locked in — use the code below on your next order.
              </p>

              <div className="bg-[#3EACDB] text-white p-8 rounded-sm mb-8" data-testid="discount-code-box">
                <p className="text-sm uppercase tracking-widest text-blue-200 mb-2">Your Discount Code</p>
                <p className="font-heading text-3xl md:text-4xl font-black tracking-wider mb-4" data-testid="discount-code-value">{result.discount_code}</p>
                <Button onClick={copyCode} variant="outline" className="bg-white text-[#3EACDB] hover:bg-zinc-100 border-white" data-testid="copy-code-btn">
                  <Copy className="mr-2 w-4 h-4" />
                  Copy Code
                </Button>
              </div>

              <p className="text-zinc-600 mb-6">
                We've also emailed this code to <strong>{formData.email}</strong> so you have it on hand.
              </p>

              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Link to="/quote">
                  <Button className="h-12 px-7 text-sm font-bold uppercase tracking-wider bg-[#3EACDB] hover:bg-[#2593C4] rounded-sm w-full sm:w-auto" data-testid="success-order-btn">
                    Place Your First Order
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </Link>
                <a href="tel:+12129711334">
                  <Button variant="outline" className="h-12 px-7 text-sm font-bold uppercase tracking-wider rounded-sm w-full sm:w-auto border-2" data-testid="success-call-btn">
                    <Phone className="mr-2 w-4 h-4" />
                    (212) 971-1334
                  </Button>
                </a>
              </div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default BusinessSignupPage;
